import QuestionContainer from "@/components/QuestionContainer";
import SlideFooter from "@/components/SlideFooter";
import { Logic, QuestionAnswer } from "@/types/questions";
import { useRef, useState } from "preact/hooks";

interface SingleSelectProps {
  onBack: (selectedAnswer: QuestionAnswer | null) => void;
  onNext: (selectedAnswer: QuestionAnswer) => void;
  questionText: string;
  answers: QuestionAnswer[];
  logic?: Logic;
  defaultSelectedAnswers: QuestionAnswer | null;
}

const SingleSelect = ({
  onBack,
  onNext,
  questionText,
  answers,
  defaultSelectedAnswers,
}: SingleSelectProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<QuestionAnswer | null>(
    defaultSelectedAnswers
  );

  const handleBack = () => {
    onBack(selectedAnswer);
  };

  const handleNext = () => {
    if (selectedAnswer) {
      onNext(selectedAnswer);
    }
  };

  const radioRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      const nextIndex = (index + 1) % answers.length;
      radioRefs.current[nextIndex]?.focus();
      setSelectedAnswer(answers[nextIndex]);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      const prevIndex = (index - 1 + answers.length) % answers.length;
      radioRefs.current[prevIndex]?.focus();
      setSelectedAnswer(answers[prevIndex]);
    } else if (e.key === " ") {
      setSelectedAnswer(answers[index]);
    }
  };

  return (
    <>
      <QuestionContainer legendText={questionText} role="radiogroup">
        <div className="lc-grid lc-grid-cols-1 md:lc-grid-cols-2 lg:lc-grid-cols-3 lc-gap-6 lc-self-center lc-justify-center lc-auto-rows-min">
          {answers.map((answer, index) => (
            <button
              key={answer.id}
              id={answer.id}
              className={`lc-flex lc-gap-4 lc-p-4 lc-cursor-pointer ${
                selectedAnswer?.id === answer.id
                  ? "lc-bg-blue-primary lc-p-[18px]"
                  : "!lc-border-solid !lc-border-2 !lc-border-cfa-blue-700"
              } lc-text-white lc-min-w-[160px] lc-text-left lc-items-center lc-leading-5 lc-min-h-[72px]`}
              type="button"
              onClick={() => setSelectedAnswer(answer)}
              onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, index)}
              role="radio"
              aria-checked={selectedAnswer?.id === answer.id}
              tabIndex={
                index === 0 && !selectedAnswer
                  ? 0
                  : selectedAnswer?.id === answer.id
                  ? 0
                  : -1
              }
              ref={(el) => (radioRefs.current[index] = el)}
            >
              <span
                className={`lc-flex-shrink-0 lc-relative lc-w-7 lc-h-7 lc-inline-block lc-rounded-full lc-cursor-pointer ${
                  selectedAnswer?.id === answer.id
                    ? "lc-bg-transparent !lc-border-solid !lc-border-2 !lc-border-white"
                    : "lc-bg-cfa-blue-700"
                }`}
              >
                <span
                  className={`lc-absolute lc-inset-1 lc-w-4 lc-h-4 lc-rounded-full ${
                    selectedAnswer?.id === answer.id
                      ? "lc-bg-white"
                      : "lc-bg-transparent"
                  }`}
                ></span>
              </span>
              <span className="lc-font-azo-sans lc-text-base">
                {answer.text}
              </span>
            </button>
          ))}
        </div>
      </QuestionContainer>
      <SlideFooter
        onNext={handleNext}
        onBack={handleBack}
        disableContinue={!selectedAnswer}
      />
    </>
  );
};

export default SingleSelect;
